import {
  Box,
  Button,
  Flex,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { formatEther, parseEther } from '@ethersproject/units';
import Balance from '@ui/components/Balance/Balance';
import Info from '@ui/components/Info/Info';
import { InputBox } from '@ui/components/InputCard/InputCard';
import { InputCardStepper } from '@ui/components/InputCard/InputCardStepper';
import { StrategyApySimpleLineChart } from '@ui/components/StrategyApySimpleLineChart';
import { pathDict } from '@ui/config/paths';
import { getPathInfo, useRealPathname } from '@ui/hooks/router';
import { useStrategyDepositTransactionFee } from '@ui/hooks/useStrategyDepositTransactionFee';
import { useTranslations } from '@ui/i18n';
import bigNumberToNumber from '@ui/utils/bigNumberToNumber';
import { formatNumber, formatPercentage, parseValue } from '@ui/utils/format';
import type { LiquidityStrategyView } from '@ui/utils/strategy';
import {
  LiquidityStrategyTokenDict,
  useDepositTokenBalances,
  useDepositTokenBalanceUsd,
  useStrategyTvl,
  useTokenAssetsFromShares,
  useVaultBalance,
} from '@ui/utils/strategy';
import { pushRouterWithoutRerender } from '@ui/utils/urlHistory';
import { balanceValidator, decimalsValidator } from '@ui/utils/validators';
import type { FC } from 'react';
import { useMemo } from 'react';

import { useDepositDrawer } from './DepositDrawer/useDepositDrawer';
import { ReceiveRows } from './ReceiveRows';
import { TokenSelectionButton } from './TokenSelectionButton';
import { useStrategyStore } from './useStrategyStore';

export const DepositAndWithdraw: FC = () => {
  const t = useTranslations();
  const pathInfo = getPathInfo(useRealPathname());
  const tvl = useStrategyTvl();

  return (
    <>
      <Flex mb={3} flexDir="column" align="center">
        <Box>
          {t('{coin} liquidity strategy', {
            coin: 'CRO',
          })}
        </Box>
        <Skeleton
          color="text-light"
          isLoaded={tvl.data !== undefined}
          textStyle="caption"
        >
          {formatNumber(tvl.data ?? 0, 2)} USD
        </Skeleton>
      </Flex>
      <Tabs
        mb="16px"
        index={pathInfo.strategyView === 'withdraw' ? 1 : 0}
        onChange={(index) => {
          const strategyView: LiquidityStrategyView = index
            ? 'withdraw'
            : 'deposit';
          pushRouterWithoutRerender(pathDict.cro.strategy[strategyView]);

          // switch to default token (see PRD)
          useStrategyStore.getState().$$resetData?.();
        }}
        role="heading"
        aria-label="strategy vaults"
        aria-level={1}
      >
        <TabList>
          <Tab>{t('Deposit')}</Tab>
          <Tab>{t('Withdraw')}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <DepositContent />
          </TabPanel>
          <TabPanel>
            <WithdrawContent />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
};

const DepositContent = () => {
  const t = useTranslations();

  const amountStr = useStrategyStore((s) => s.amountStr);
  const amountInputError = useStrategyStore((s) => s.amountInputError);
  const depositToken = useStrategyStore((s) => s.depositToken);
  const openDepositDrawer = useDepositDrawer((s) => s.onOpen);
  const depositTransactionFee = useStrategyDepositTransactionFee(depositToken);

  const tokenInfo = LiquidityStrategyTokenDict[depositToken];

  /**
   * todo cover.lv mock
   */
  const apy = 0.24;
  /**
   * todo cover.lv mock
   */
  const apyList: {
    y: number;
    date: number;
  }[] = [
    {
      y: 0.1,
      date: Date.now(),
    },
    {
      y: 1.2,
      date: Date.now(),
    },
  ];
  const depositFee = 0;

  const balanceRet = useDepositTokenBalances()[depositToken];
  const balanceUsd = useDepositTokenBalanceUsd({
    token: depositToken,
    amount: balanceRet.data,
  });

  const amountUsd = useDepositTokenBalanceUsd({
    token: depositToken,
    amount: parseValue(
      amountStr,
      LiquidityStrategyTokenDict[depositToken].decimals,
    ),
  });

  const maxAmount = useMemo(() => {
    const balance = balanceRet.data ? bigNumberToNumber(balanceRet.data) : 0;
    if (!depositTransactionFee) return 0;
    if (depositTransactionFee.lte(0)) return balance;
    return parseFloat(
      formatEther(parseEther(balance.toString()).sub(depositTransactionFee)),
    );
  }, [balanceRet.data, depositTransactionFee]);

  const onChange = (value: string) => {
    useStrategyStore.setState({
      amountStr: value,
      amountInputError:
        decimalsValidator(value) || balanceValidator(value, maxAmount),
    });
  };

  return (
    <>
      <Balance
        title={t('{coin} Wallet Balance', {
          coin: tokenInfo.name,
        })}
        icon={<tokenInfo.Icon width="32px" height="32px" />}
        amount={balanceRet.data}
        usdAmount={balanceUsd}
        usdDisplayDecimals={3}
        showUsdAmount
        mt="16px"
      />
      <StrategyApySimpleLineChart apyList={apyList} mt="30px" />
      <Info
        title={t('Liquidity strategy APY')}
        value={formatPercentage(apy)}
        mt="40px"
      />
      <Box mt="16px">
        <InputCardStepper
          max={maxAmount}
          maxDecimals={tokenInfo.maxDecimals}
          value={amountStr}
          onChange={onChange}
          mb="6px"
        />
        <Flex gap="8px">
          <InputBox
            flex="1"
            max={maxAmount}
            maxDecimals={tokenInfo.maxDecimals}
            label={t('Input {coin} amount', {
              coin: tokenInfo.name,
            })}
            value={amountStr}
            onChange={onChange}
            errorMessage={amountInputError}
            warningMessage={
              amountStr === String(maxAmount) && depositToken === 'cro'
                ? t('Gas fee has been deducted from maximum balance')
                : undefined
            }
            usdValue={amountUsd ? bigNumberToNumber(amountUsd) : undefined}
          />
          <TokenSelectionButton
            width="82px"
            value={depositToken}
            onChange={(depositToken) => {
              useStrategyStore.setState({
                depositToken,
                amountStr: '',
                amountInputError: '',
              });
            }}
          />
        </Flex>
      </Box>
      <Button
        w="full"
        disabled={!amountStr || !!amountInputError}
        onClick={() => {
          openDepositDrawer({
            isWithdraw: false,
            token: depositToken,
            amountStr,
          });
          useStrategyStore.setState({
            amountStr: '',
            amountInputError: '',
          });
        }}
        mt="16px"
      >
        {t('Deposit {coin}', {
          coin: tokenInfo.name,
        })}
      </Button>
      <ReceiveRows
        isWithdraw={false}
        token={depositToken}
        amountStr={amountStr}
        mt="28px"
      />
      <Info
        title={t('Deposit Fee')}
        value={formatPercentage(depositFee)}
        mt="16px"
      />
      <Box p="10px 8px" mt="28px" bg="brand-white" rounded="4px">
        <Text as="span" textStyle="bodyBold" color="warning">
          Attention:
        </Text>{' '}
        Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
        tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim
        veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea
        commodo consequat.
      </Box>
    </>
  );
};

const WithdrawContent = () => {
  const t = useTranslations();

  const withdrawStr = useStrategyStore((s) => s.withdrawStr);
  const withdrawInputError = useStrategyStore((s) => s.withdrawInputError);
  const withdrawToken = useStrategyStore((s) => s.withdrawToken);
  const openDepositDrawer = useDepositDrawer((s) => s.onOpen);

  const vaultBalanceRet = useVaultBalance(withdrawToken);
  const balanceBn = useTokenAssetsFromShares(
    withdrawToken,
    vaultBalanceRet.data,
  );
  const balanceUsd = useDepositTokenBalanceUsd({
    token: withdrawToken,
    amount: balanceBn,
  });
  const withdrawAmountUsd = useDepositTokenBalanceUsd({
    token: withdrawToken,
    amount: parseValue(
      withdrawStr,
      LiquidityStrategyTokenDict[withdrawToken].decimals,
    ),
  });

  const tokenInfo = LiquidityStrategyTokenDict[withdrawToken];
  const maxAmount = balanceBn ? bigNumberToNumber(balanceBn) : 0;
  const withdrawFee = 0;

  const onChange = (value: string) => {
    useStrategyStore.setState({
      withdrawStr: value,
      withdrawInputError:
        decimalsValidator(value) || balanceValidator(value, maxAmount),
    });
  };

  return (
    <>
      <Balance
        title={t('{coin} deposit balance', {
          coin: tokenInfo.name,
        })}
        icon={<tokenInfo.Icon width="32px" height="32px" />}
        amount={balanceBn}
        usdAmount={balanceUsd}
        usdDisplayDecimals={3}
        showUsdAmount
        mt="16px"
      />
      <Box mt="32px">
        <InputCardStepper
          max={maxAmount}
          maxDecimals={tokenInfo.maxDecimals}
          value={withdrawStr}
          onChange={onChange}
          mb="6px"
        />
        <Flex gap="8px">
          <InputBox
            flex="1"
            max={maxAmount}
            maxDecimals={tokenInfo.maxDecimals}
            label={t('Input {coin} amount', {
              coin: tokenInfo.name,
            })}
            value={withdrawStr}
            onChange={onChange}
            errorMessage={withdrawInputError}
            usdValue={
              withdrawAmountUsd
                ? bigNumberToNumber(withdrawAmountUsd)
                : undefined
            }
          />
          <TokenSelectionButton
            width="82px"
            value={withdrawToken}
            onChange={(withdrawToken) => {
              useStrategyStore.setState({
                withdrawToken: withdrawToken,
              });
            }}
            preferred="ferroLP"
          />
        </Flex>
      </Box>
      <Button
        w="full"
        disabled={!withdrawStr || !!withdrawInputError}
        onClick={() => {
          openDepositDrawer({
            isWithdraw: true,
            token: withdrawToken,
            amountStr: withdrawStr,
          });
          useStrategyStore.setState({
            withdrawStr: '',
            withdrawInputError: '',
          });
        }}
        mt="16px"
      >
        {t('Withdraw {coin}', {
          coin: tokenInfo.name,
        })}
      </Button>
      <ReceiveRows
        isWithdraw
        token={withdrawToken}
        amountStr={withdrawStr}
        mt="28px"
      />
      <Info
        title={t('Withdrawal Fee')}
        value={formatPercentage(withdrawFee)}
        mt="16px"
      />
    </>
  );
};
